import { Amount } from "./amount.js";
import { Include, ValuesOf } from "./typeutils.js";

export const ZeroVAT = 7;
export const WithoutVAT = 8;
export const VATRate24 = 1;
export const VATRate13 = 2;
export const VATRate6 = 3;
export const VATRate17 = 4;
export const VATRate9 = 5;
export const VATRate4 = 6;

/**
 * 8.2 Κατηγορία Φ.Π.Α.
 */
export const nonZeroVATCategory = {
  VATRate24,
  VATRate13,
  VATRate6,
  VATRate17,
  VATRate9,
  VATRate4,
} as const;

export type VATCategoryNonZeroVAT = ValuesOf<typeof nonZeroVATCategory>;
export const isVATCategoryNonZero = (
  input: unknown
): input is VATCategoryNonZeroVAT =>
  typeof input === "number" &&
  Object.values(nonZeroVATCategory).includes(input as any);

export const vatCategoryZeroVAT = { ZeroVAT } as const;

export type VATCategoryZeroVAT = ValuesOf<typeof vatCategoryZeroVAT>;

export const vatCategoryWithoutVAT = { WithoutVAT } as const;

export type VATCategoryWithoutVAT = ValuesOf<typeof vatCategoryWithoutVAT>;

export const vatCategoryWithRate = {
  ...nonZeroVATCategory,
  ...vatCategoryZeroVAT,
} as const;
export const vatCategory = {
  ...nonZeroVATCategory,
  ...vatCategoryZeroVAT,
  ...vatCategoryWithoutVAT,
} as const;

export type VATCategory = ValuesOf<typeof vatCategory>;
export const isVATCategory = (input: unknown): input is VATCategoryNonZeroVAT =>
  typeof input === "number" &&
  Object.values(vatCategory).includes(input as any);

/**
 * Συντελεστές ΦΠΑ
 */
export const vatRates = {
  [VATRate24]: 24.0,
  [VATRate13]: 13.0,
  [VATRate6]: 6.0,
  [VATRate17]: 17.0,
  [VATRate9]: 9.0,
  [VATRate4]: 4.0,
  [ZeroVAT]: 0.0,
  [WithoutVAT]: 0.0,
} as const;

/**
 * Κατηγορία Φ.Π.Α. με συντελεστή
 */
export type VATCategoryWithRate = keyof typeof vatRates;
export type VATRate = ValuesOf<typeof vatRates>;
export const isVATCategoryWithRate = (
  input: unknown
): input is VATCategoryWithRate => Object.keys(vatRates).includes(`${input}`);
export const vatCategoryRate = (cat: unknown) =>
  isVATCategoryWithRate(cat) ? vatRates[cat] : undefined;

/**
 * 8.3 Κατηγορία Αιτίας Εξαίρεσης ΦΠΑ
 */
export const vatExemption = {
  /** Άρθρο 2 και 3 - Εξαιρούμενες οντότητες και δραστηριότητες */
  Article2And3: 1,
  /**
   * Άρθρο 5 - Παράδοση αγαθών
   */
  Article5: 2,
  /**
   * Άρθρο 13 - Τόπος παράδοσης αγαθών
   */
  Article13: 3,
  /**
   * Άρθρο 14 - Τόπος παροχής υπηρεσιών
   */
  Article14: 4,
  /**
   * Άρθρο 16
   *
   * Χρόνος γένεσης της φορολογικής υποχρέωσης στην παράδοση αγαθών
   * και στην παροχή υπηρεσιών
   */
  Article16: 5,
  /**
   * Άρθρο 19 - Φορολογητέα αξία στην παράδοση αγαθών,
   * στην ενδοκοινοτική απόκτηση αγαθών και στην παροχή υπηρεσιών
   */
  Article19: 6,
  /**
   * Άρθρο 22 - Απαλλαγές στο εσωτερικό της χώρας
   */
  Article22: 7,
  /**
   * Άρθρο 24. Απαλλαγές των πράξεων κατά την εξαγωγή, εκτός Κοινότητας,
   * των εξομοιούμενων προς αυτές πράξεων και των διεθνών μεταφορών
   */
  Article24: 8,
  /**
   * Άρθρο 25. Απαλλαγές στη διεθνή διακίνηση αγαθών
   */
  Article25: 9,
  /**
   * Άρθρο 26. Απαλλαγές στο καθεστώς των φορολογικών αποθηκών,
   * άλλων από αυτές του Ν. 2960/2001
   */
  Article26: 10,
  /** Άρθρο 27. Ειδικές Απαλλαγές */
  Article27: 11,
  /** Άρθρο 27. Ειδικές Απαλλαγές - Πλοία */
  Article27Ships: 12,
  /** Άρθρο 27.1.γ Ειδικές Απαλλαγές - Εφοδιασμός Πλοία */
  Article27_1_C_Ships: 13,
  /** Άρθρο 28 - Απαλλαγές στην Παράδοση αγαθών σε άλλο κράτος-μέλος */
  Article28: 14,
  /** Άρθρο 39 - Ειδικό καθεστώς μικρών επιχειρήσεων */
  Artile39: 15,
  /**
   * Άρθρο 39a - Ειδικό καθεστώς καταβολής του φόρου από τον λήπτη
   * αγαθών και υπηρεσιών.
   *
   */
  Article39a: 16,
  /** Άρθρο 40 - Ειδικό καθεστώς κατ' αποκοπή καταβολής του φόρου */
  Article40: 17,
  /** Άρθρο 41 - Ειδικό καθεστώς αγροτών */
  Article41: 18,
  /** Άρθρο 47 - Ειδικό καθεστώς επενδυτικού χρυσού */
  Article47: 19,
  /** Άρθρο 43 Εμπεριεχόμενος - Ειδικό καθεστώς πρακτορείων ταξιδιών  */
  Article43: 20,
  /** Άρθρο 44 Εμπεριεχόμενος - Ειδικό καθεστώς φορολογίας
   * βιομηχανοποιημένων καπνών  */
  Article44: 21,
  /**
   * Άρθρο 45 Εμπεριεχόμενος - Ειδικό καθεστώς φορολογίας των υποκειμένων
   * στο φόρο μεταπωλητών που παραδίδουν μεταχειρισμένα αγαθά
   * και αντικείμενα καλλιτεχνικής, συλλεκτικής ή αρχαιολογικής αξίας
   */
  Article45: 22,
  /**
   * Άρθρο 46 Εμπεριεχόμενος -
   * Ειδικό καθεστώς φορολογίας για τις πωλήσεις σε δημοπρασία
   */
  Article46: 23,
  // MyData SendInvoice accepts values up to 23
  /** Άρθρο 6 - Παράδοση ακινήτων */
  Article6: 24,
  /** ΠΟΛ_1029_1995 */
  POL_1029_1995: 25,
  /** ΠΟΛ_1167_2015 */
  POL_1167_2015: 26,
  /** ΛοιπέςΕξαιρέσειςΦΠΑ */
  OthervatExemption: 27,
} as const;

/**
 * 8.3 Κατηγορία Αιτίας Εξαίρεσης ΦΠΑ
 */
export type vatExemption = ValuesOf<typeof vatExemption>;
export const isvatExemption = (input: unknown): input is vatExemption =>
  typeof input === "number" &&
  Object.values(vatExemption).includes(input as any);

/**
 * 8.3 Κατηγορία Αιτίας Εξαίρεσης ΦΠΑ
 *
 * Επιτρεπόμενες τιμές ανά γραμμή παραστατικού
 */
export type InvoicevatExemption = Exclude<vatExemption, 24 | 25 | 26 | 27>;

/**
 *  8.4 Κατηγορία Παρακρατούμενων Φόρων
 */
export const withheldTax = {
  Τόκοι: 1,
  Δικαιώματα: 2,
  ΑμοιβέςΣυμβουλώνΔιοίκησης: 3,
  ΤεχνικάΈργα: 4,
  ΥγράΚαύσιμαΚαιΠροϊόνταΚαπνοβιομηχανίας: 5,
  ΛοιπάΑγαθά: 6,
  ΠαροχήΥπηρεσιών: 7,
  ΑμοιβέςΜελέτηςΑρχιτεκτόνωνΚαιΜηχανικών: 8,
  ΑμοιβέςΈργουΑρχιτεκτόνωνΚαιΜηχανικών: 9,
  ΑμοιβέςΔικηγόρων: 10,
  ΜισθωτέςΥπηρεσίες: 11,
  ΜισθωτέςΥπηρεσίεςΑξιωματικώνΕμπορικούΝαυτικού: 12,
  ΜισθωτέςΥπηρεσίεςΚατώτεροΠλήρωμαΕμπορικούΝαυτικού: 13,
  ΕιδικήΕισφοράΑλληλεγγύης: 14,
  ΑποζημίωσηΛογωΔιακοπήςΣχέσηςΕργασίας: 15,
  ΣυναλλαγέςΑλλοδαπήςΣΑΔΦ: 16,
  ΛοιπέςΠαρακρατήσειςΦόρου: 17,
} as const;

/**
 *  8.4 Κατηγορία Παρακρατούμενων Φόρων
 */
export type WithheldTax = ValuesOf<typeof withheldTax>;

/**
 *  8.4 Κατηγορία Παρακρατούμενων Φόρων
 *
 * Επιτρεπόμενες τιμές ανά γραμμή παραστατικού
 */
export type InvoiceWithheldTax = Exclude<WithheldTax, 16 | 17>;

/**
 * Συντελεστές Παρακρατούμενων Φόρων
 */
export const withheldTaxRates = {
  [withheldTax.Τόκοι]: 15.0,
  [withheldTax.Δικαιώματα]: 20.0,
  [withheldTax.ΑμοιβέςΣυμβουλώνΔιοίκησης]: 20.0,
  [withheldTax.ΤεχνικάΈργα]: 3.0,
  [withheldTax.ΥγράΚαύσιμαΚαιΠροϊόνταΚαπνοβιομηχανίας]: 1,
  [withheldTax.ΛοιπάΑγαθά]: 4.0,
  [withheldTax.ΠαροχήΥπηρεσιών]: 8.0,
  [withheldTax.ΑμοιβέςΜελέτηςΑρχιτεκτόνωνΚαιΜηχανικών]: 4.0,
  [withheldTax.ΑμοιβέςΈργουΑρχιτεκτόνωνΚαιΜηχανικών]: 10.0,
  [withheldTax.ΑμοιβέςΔικηγόρων]: 15.0,
  [withheldTax.ΜισθωτέςΥπηρεσίεςΑξιωματικώνΕμπορικούΝαυτικού]: 15.0,
  [withheldTax.ΜισθωτέςΥπηρεσίεςΚατώτεροΠλήρωμαΕμπορικούΝαυτικού]: 10.0,
} as const;

export type WithheldTaxWithRate = keyof typeof withheldTaxRates;
export const isWithheldTaxWithRate = (c: unknown): c is WithheldTaxWithRate =>
  typeof c === "number" && Reflect.ownKeys(withheldTaxRates).includes(c as any);

/**
 *  8.5 Κατηγορία Λοιπών Φόρων
 */
export const otherTax = {
  FireInsurranceA1: 1,
  FireInsuranceA2: 2,
  LifeInsurance: 3,
  OtherInsurance: 4,
  TaxFreeInsurance: 5,
  HotelsUpToTwoStars: 6, // 0.50
  HotelsThreeStars: 7, // 1.50
  HotelsFourStars: 8, // 3.00
  HotelsFiveStars: 9, // 3.00
  RoomsToLet: 10, // 0.50
  TVAdvertisement: 11, // 5%
  LuxuryTaxImported: 12, // 10%
  LuxuryTaxDomestic: 13, // 10%
  CasinoTickets: 14, // 80%
  // BUG: MyData does not allow value 15 for otherTax
  // and values 1 and 2 are deprecated
  FireInsurance: 15, // 20%
  // These declare arbitrary amounts and are not calculated by rate or item
  Customs: 16,
  OtherTaxes: 17,
  OtherTaxCharges: 18,
} as const;

/**
 * 8.5 Κατηγορία Λοιπών Φόρων
 */
export type OtherTax = ValuesOf<typeof otherTax>;

/**
 *
 * 8.5 Κατηγορία Λοιπών Φόρων
 *
 * Επιτρεπόμενες τιμές ανά γραμμή παραστατικού
 */
export type InvoiceOtherTax = Exclude<OtherTax, 15 | 16 | 17 | 18>;

/**
 * Συντελεστές Λοιπών Φόρων
 */
export const otherTaxRates = {
  [otherTax.FireInsurranceA1]: 15.0,
  [otherTax.FireInsuranceA2]: 5.0,
  [otherTax.LifeInsurance]: 4.0,
  [otherTax.OtherInsurance]: 15.0,
  [otherTax.TaxFreeInsurance]: 0.0,
  [otherTax.TVAdvertisement]: 5.0,
  [otherTax.LuxuryTaxImported]: 10.0,
  [otherTax.LuxuryTaxDomestic]: 10.0,
  [otherTax.CasinoTickets]: 80.0,
  [otherTax.FireInsurance]: 20.0,
} as const;

export type OtherTaxWithRate = keyof typeof otherTaxRates;
export const isOtherTaxWithRate = (tax: unknown): tax is OtherTaxWithRate =>
  typeof tax === "number" &&
  Reflect.ownKeys(otherTaxRates).includes(tax as any);

/**
 * Ποσά ανα τεμάχιο Λοιπών Φόρων
 */
export const otherTaxItem = {
  [otherTax.HotelsUpToTwoStars]: 0.5,
  [otherTax.HotelsThreeStars]: 1.5,
  [otherTax.HotelsFourStars]: 3.0,
  [otherTax.HotelsFiveStars]: 4.0,
  [otherTax.RoomsToLet]: 0.5,
} as const;

export type OtherTaxPerItem = keyof typeof otherTaxItem;
export const isOtherTaxPerItem = (tax: unknown): tax is OtherTaxPerItem =>
  typeof tax === "number" && Reflect.ownKeys(otherTaxItem).includes(tax as any);

/**
 * 8.6 Κατηγορία Συντελεστή Χαρτοσήμου
 */
export const stampDuty = {
  Rate_1_60: 1,
  Rate_2_40: 2,
  Rate_3_60: 3,
  OtherStampDuty: 4,
} as const;

/**
 * 8.6 Κατηγορία Συντελεστή Χαρτοσήμου
 */
export type StampDuty = ValuesOf<typeof stampDuty>;
/**
 * 8.6 Κατηγορία Συντελεστή Χαρτοσήμου
 *
 * Επιτρεπόμενες τιμές ανά γραμμή παραστατικού
 */
export type InvoiceStampDuty = Exclude<StampDuty, 4>;

/**
 * Συντελεστές Χαρτοσήμου
 */
export const stampDutyRates = {
  [stampDuty.Rate_1_60]: 1.2,
  [stampDuty.Rate_2_40]: 2.4,
  [stampDuty.Rate_3_60]: 3.6,
} as const;

export type StampDutyRate = ValuesOf<typeof stampDutyRates>;

export const isRateStampDuty = (s: unknown): s is StampDutyRate =>
  typeof s === "number" && Reflect.ownKeys(feeRates).includes(s as any);
/**
 * 8.7 Κατηγορία Τελών
 */
export const fee = {
  /** */
  MonthlyBillUpTo50EUR: 1,
  MonthlyBill50To100EUR: 2,
  MonthlyBill100To150EUR: 3,
  MonthlyBillOver150EUR: 4,
  TopUpFee: 5,
  TVSubscription: 6,
  LandLine: 7,
  PlasticBag: 8,
  Dakoktonia2Percent: 9,
  OtherFees: 10,
  OtherTaxFees: 11,
  DakoktoniaFee: 12,
} as const;

/**
 * 8.7 Κατηγορία Τελών
 */
export type Fee = ValuesOf<typeof fee>;

/**
 * Ποσά Τελών ανά τεμάχιο
 */
export const feeItems = {
  [fee.PlasticBag]: 0.07,
} as const;

export type FeeItem = keyof typeof feeItems;
export const isPerItemFee = (fee: unknown): fee is FeeItem =>
  typeof fee === "number" && Reflect.ownKeys(feeItems).includes(fee as any);

/**
 * Συντελεστές Τελών
 */
export const feeRates = {
  [fee.MonthlyBillUpTo50EUR]: 12.0,
  [fee.MonthlyBill50To100EUR]: 15.0,
  [fee.MonthlyBill100To150EUR]: 18.0,
  [fee.MonthlyBillOver150EUR]: 20.0,
  [fee.TopUpFee]: 12.0,
  [fee.TVSubscription]: 10.0,
  [fee.LandLine]: 5.0,
  [fee.Dakoktonia2Percent]: 2.0,
} as const;

export type FeeRate = keyof typeof feeRates;
export const isRateFee = (fee: unknown): fee is FeeRate =>
  typeof fee === "number" && Reflect.ownKeys(feeRates).includes(fee as any);

/**
 * 8.7 Κατηγορία Τελών
 *
 * Επιτρεπόμενες τιμές ανά γραμμή παραστατικού
 */
export type InvoiceFee = FeeItem | FeeRate;

export const taxType = {
  /** Παρακρατούμενοι Φόροι */
  Withheld: 1,
  /** Τέλη */
  Fee: 2, //
  /** Λοιποί Φόροι */
  OtherTax: 3,
  /** Χαρτόσημο */
  StampDuty: 4,
  /** Κρατήσεις */
  Deductions: 5,
} as const;

export type TaxType = ValuesOf<typeof taxType>;
export const isTaxType = (input: unknown): input is TaxType =>
  typeof input === "number" && Object.values(taxType).includes(input as any);
export const taxTypeNames = {
  [taxType.Withheld]: "Παρακρατούμενοι Φόροι",
  [taxType.Fee]: "Τέλη",
  [taxType.OtherTax]: "Λοιποί Φόροι",
  [taxType.StampDuty]: "Χαρτόσημο",
  [taxType.Deductions]: "Κρατήσεις",
} as const;

export type TaxTotals =
  | WithheldTaxTotals
  | FeeTotals
  | OtherTaxTotals
  | StampDutyTotals
  | DeductionTotals;

type WithheldTaxTotals =
  | {
      taxType: typeof taxType.Withheld;
      taxCategory: WithheldTaxWithRate;
      underlyingValue: Amount;
      taxAmount: Amount;
    }
  | {
      taxType: typeof taxType.Withheld;
      taxCategory: Exclude<WithheldTax, WithheldTaxWithRate>;
      taxAmount: Amount;
    };

type FeeTotals =
  | {
      taxType: typeof taxType.Fee;
      taxCategory: FeeRate;
      underlyingValue: Amount;
      taxAmount: Amount;
    }
  | {
      taxType: typeof taxType.Fee;
      taxCategory: FeeItem;
      taxAmount: Amount;
    };

type OtherTaxTotals =
  | {
      taxType: typeof taxType.OtherTax;
      taxCategory: Include<OtherTax, OtherTaxWithRate>;
      underlyingValue: Amount;
      taxAmount: Amount;
    }
  | {
      taxType: typeof taxType.OtherTax;
      taxCategory: Include<OtherTax, OtherTaxPerItem>;
      taxAmount: Amount;
    };

type StampDutyTotals = {
  taxType: typeof taxType.StampDuty;
  taxCategory: StampDuty;
  underlyingValue: Amount;
  taxAmount: Amount;
};

type DeductionTotals = {
  taxType: typeof taxType.Deductions;
  taxAmount: Amount;
};

type DeductedTax = typeof taxType.Deductions | typeof taxType.Withheld;
export const isDeductedTax = (input: unknown): input is DeductedTax =>
  input === taxType.Deductions || input === taxType.Withheld;

/**
 * 8.3 Κατηγορία Αιτίας Εξαίρεσης ΦΠΑ
 */
export const vatExemptionDescriptions = {
  [vatExemption.Article2And3]:
    "Άρθρο 2 και 3 - Εξαιρούμενες οντότητες και δραστηριότητες",
  [vatExemption.Article5]: "Άρθρο 5 - Παράδοση αγαθών",
  [vatExemption.Article13]: "Άρθρο 13 - Τόπος παράδοσης αγαθών",
  [vatExemption.Article14]: "Άρθρο 14 - Τόπος παροχής υπηρεσιών",
  [vatExemption.Article16]:
    "Άρθρο 16 Χρόνος γένεσης της φορολογικής υποχρέωσης στην παράδοση αγαθών και στην παροχή υπηρεσιών",
  [vatExemption.Article19]:
    "Άρθρο 19 - Φορολογητέα αξία στην παράδοση αγαθών, στην ενδοκοινοτική απόκτηση αγαθών και στην παροχή υπηρεσιών",
  [vatExemption.Article22]: "Άρθρο 22 - Απαλλαγές στο εσωτερικό της χώρας",
  [vatExemption.Article24]:
    "Άρθρο 24. Απαλλαγές των πράξεων κατά την εξαγωγή, εκτός Κοινότητας, των εξομοιούμενων προς αυτές πράξεων και των διεθνών μεταφορών",
  [vatExemption.Article25]: "Άρθρο 25. Απαλλαγές στη διεθνή διακίνηση αγαθών",
  [vatExemption.Article26]:
    "Άρθρο 26. Απαλλαγές στο καθεστώς των φορολογικών αποθηκών, άλλων από αυτές του Ν. 29602001",
  [vatExemption.Article27]: "Άρθρο 27. Ειδικές Απαλλαγές",
  [vatExemption.Article27Ships]: "Άρθρο 27. Ειδικές Απαλλαγές - Πλοία",
  [vatExemption.Article27_1_C_Ships]:
    "Άρθρο 27.1.γ Ειδικές Απαλλαγές - Εφοδιασμός Πλοία",
  [vatExemption.Article28]:
    "Άρθρο 28 - Απαλλαγές στην Παράδοση αγαθών σε άλλο κράτος-μέλος",
  [vatExemption.Artile39]: "Άρθρο 39 - Ειδικό καθεστώς μικρών επιχειρήσεων",
  [vatExemption.Article39a]:
    "Άρθρο 39a - Ειδικό καθεστώς καταβολής του φόρου από τον λήπτη αγαθών και υπηρεσιών.",
  [vatExemption.Article40]:
    "Άρθρο 40 - Ειδικό καθεστώς κατ' αποκοπή καταβολής του φόρου",
  [vatExemption.Article41]: "Άρθρο 41 - Ειδικό καθεστώς αγροτών",
  [vatExemption.Article47]: "Άρθρο 47 - Ειδικό καθεστώς επενδυτικού χρυσού",
  [vatExemption.Article43]:
    "Άρθρο 43 Εμπεριεχόμενος - Ειδικό καθεστώς πρακτορείων ταξιδιών ",
  [vatExemption.Article44]:
    "Άρθρο 44 Εμπεριεχόμενος - Ειδικό καθεστώς φορολογίας βιομηχανοποιημένων καπνών ",
  [vatExemption.Article45]:
    "Άρθρο 45 Εμπεριεχόμενος - Ειδικό καθεστώς φορολογίας των υποκειμένων στο φόρο μεταπωλητών που παραδίδουν μεταχειρισμένα αγαθά και αντικείμενα καλλιτεχνικής, συλλεκτικής ή αρχαιολογικής αξίας",
  [vatExemption.Article46]:
    "Άρθρο 46 Εμπεριεχόμενος - Ειδικό καθεστώς φορολογίας για τις πωλήσεις σε δημοπρασία",
  [vatExemption.Article6]: "Άρθρο 6 - Παράδοση ακινήτων",
  [vatExemption.POL_1029_1995]: "ΠΟΛ 1029/1995",
  [vatExemption.POL_1167_2015]: "ΠΟΛ 1167/2015",
  [vatExemption.OthervatExemption]: "Λοιπές Εξαιρέσεις Φ.Π.Α.",
} as const;
